import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { QUERY_KEYS } from '@/constants';
import { Role } from '@/enums';
import {
  IExpiredPasswordUpdatePayload,
  IForgotPasswordPayload,
  ILoginWithCredentialsPayload,
  IOTPVerificationPayload,
  ISetupPasswordPayload,
} from '@/interfaces';
import { AuthService } from '@/services';
import { hasPermission, PermissionMetadata } from '@/utils';

export const useLoginWithCredentials = () => {
  return useMutation({
    mutationFn: async (payload: ILoginWithCredentialsPayload) =>
      await AuthService.loginWithCredentials(payload),
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (payload: IForgotPasswordPayload) =>
      await AuthService.forgotPassword(payload),
  });
};

export const usePasswordResetVerifyMutation = () => {
  return useMutation({
    mutationFn: async (payload: ISetupPasswordPayload) =>
      await AuthService.passwordResetVerify(payload),
  });
};

export const useExpiredPasswordUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: IExpiredPasswordUpdatePayload) =>
      await AuthService.expiredPasswordUpdate(payload),
  });
};

export const useLogout = () => {
  return useMutation({
    mutationFn: async () => await AuthService.logout(),
  });
};

export const useInitiatePasswordMutation = () => {
  return useMutation({
    mutationFn: async (payload: ISetupPasswordPayload) =>
      await AuthService.initiatePassword(payload),
  });
};

export const useCurrentProfile = (isEnabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.user.getCurrentProfile,
    queryFn: () => AuthService.getCurrentProfile(),
    enabled: isEnabled,
  });
};

export const usePermission = () => {
  const { data: profile, isLoading } = useCurrentProfile();
  const rolesData = profile?.roles as Role[];
  const roles = useMemo(() => (rolesData?.length ? rolesData : [Role.Unknown]), [rolesData]);

  const isGranted = useCallback(
    (input: PermissionMetadata) => hasPermission(roles, input),
    [roles],
  );

  return { isGranted, isLoading, roles };
};

export const useResendOTP = () => {
  return useMutation({
    mutationFn: async (email: string) => await AuthService.ResendOTP(email),
  });
};

export const useVerifyOTP = () => {
  return useMutation({
    mutationFn: async (payload: IOTPVerificationPayload) => await AuthService.VerifyOTP(payload),
  });
};
